import type { App } from 'vue';
import { AFF_PREFLIGHT_ATTR, AFF_PORTAL_TARGET_ID } from './mfe-scope';

/**
 * Host-side AFF (ABAX Frontend Federation) integration — the boundary half of
 * the cascade contract, decoupled from the federation slug.
 *
 * The federation installer ({@link './aff-scope'}) couples two attributes:
 * `data-mfe` (the federation utility scope) and `data-aff` (the Tailwind
 * Preflight boundary). A HOST is not a federated bundle — it has no `data-mfe`
 * slug, only the `data-aff` boundary. These primitives stamp the boundary
 * alone, so the shell opts into scoped Preflight without writing a bogus
 * `data-mfe` that no scoped CSS matches.
 *
 * Boundary stamps are OWNERSHIP-TRACKED: cleanup removes only a `data-aff`
 * this module set, never a pre-existing hand-written one (e.g. a host's literal
 * `shell-chrome[data-aff]` root). The federation installer's `unstampAffScope`
 * strips both attributes whenever the slug matches; a boundary-only stamp must
 * not inherit that footgun.
 */

/**
 * Elements whose `data-aff` THIS module set. A WeakSet so a detached portal /
 * teleport node is garbage-collected without leaking. Cleanup strips the
 * boundary only for tracked elements, leaving host-authored literals intact.
 */
const ownedBoundaries = new WeakSet<Element>();

/**
 * Stamp the AFF Preflight boundary (`data-aff` only) idempotently. Never writes
 * `data-mfe`. No-op for non-element nodes. The element is recorded as ours so a
 * later {@link unstampAffBoundary} can remove it without touching a pre-existing
 * hand-written boundary.
 */
export function stampAffBoundary(el: Element | null | undefined): void {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
  if (!el.hasAttribute(AFF_PREFLIGHT_ATTR)) {
    el.setAttribute(AFF_PREFLIGHT_ATTR, '');
    ownedBoundaries.add(el);
  }
}

/**
 * Remove the AFF Preflight boundary from an element ONLY if this module stamped
 * it. A boundary that pre-existed our stamp (host literal) is never removed.
 */
export function unstampAffBoundary(el: Element | null | undefined): void {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
  if (ownedBoundaries.has(el)) {
    el.removeAttribute(AFF_PREFLIGHT_ATTR);
    ownedBoundaries.delete(el);
  }
}

/**
 * Resolve the host portal sink, creating and owning it on `<body>` when absent.
 * Idempotent: a second call returns the existing node and re-asserts the
 * boundary. The node is the single sink for `@abax/ui` / reka-ui overlays
 * ({@link AFF_PORTAL_TARGET_ID}) and, via {@link './aff-teleport'} AffTeleport,
 * for the host's own body-teleported surfaces.
 *
 * NOTE on stacking: this node carries NO layout/stacking CSS by contract. It
 * must not gain `transform` / `z-index` / `opacity` / `filter` / `isolation` —
 * teleported `position: fixed` overlays inside it resolve against the viewport
 * and compete in the root stacking context. A stacking property here would
 * reparent that context and reorder every overlay.
 */
export function ensureAffPortalRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let root = document.getElementById(AFF_PORTAL_TARGET_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = AFF_PORTAL_TARGET_ID;
    document.body.appendChild(root);
  }
  stampAffBoundary(root);
  return root;
}

export interface InstallAffHostOptions {
  /**
   * Register and own `#aff-portal-root`. Default true. Set false for a host
   * that renders the portal sink itself and only needs the boundary helpers.
   */
  portalRoot?: boolean;
}

/**
 * Install host-side AFF integration. Registers and owns `#aff-portal-root` so
 * the Preflight boundary is declared once, early, from the host bootstrap —
 * before any overlay resolves its portal target.
 *
 * Boundary-only by construction: it does NOT resolve or stamp a `data-mfe`
 * federation slug (a host has none), and does NOT install `observeAffPortal`
 * (a dev-only MFE-mount diagnostic, not a host concern). The `app` argument is
 * accepted for call-site symmetry and future use; the current contract is the
 * portal sink. `@abax/ui` resolves the sink via DOM lookup (it spans Vue app
 * boundaries that provide/inject cannot), so no app-level provide is needed.
 */
export function installAffHost(_app?: App, opts: InstallAffHostOptions = {}): void {
  if (opts.portalRoot !== false) {
    ensureAffPortalRoot();
  }
}
